import { IVectorLayerGroupConfig } from "@esdm/map/dist/layers/groups/vector/interfaces/IVectorLayerGroupConfig";
import { WfsParamSourceMapServer, IWfsParamSource, WfsBboxAsWktRequestParams } from "@esdm/map/dist/layers/vector/sources/wfs";
import { VectorLayerStrategy } from "@esdm/map/dist/layers/vector/VectorLayerStrategy";
import { KentRecordType, KentRecordSubType } from "./KentRecordTypes";
import { IWmsLayerGroupConfig } from "@esdm/map/dist/layers/groups/wms/interfaces/IWmsLayerGroupConfig";
import { FeatureAdd } from "@esdm/map/dist/layers/vector/sources/loaders";

export class DefaultLayerConfigurationFactory {

    private mapFileUrl: string = "/api/OgcService/hbsmr";

    private paramSource(): IWfsParamSource {
        return new WfsParamSourceMapServer(new WfsBboxAsWktRequestParams(),{srsName: "EPSG:27700"});
    }

    constructor() {
    }

    public layerConfig = (
        recordType: KentRecordType,
        recordSubType: KentRecordSubType = null,
        opts: IVectorLayerGroupConfig = {}
    ): IVectorLayerGroupConfig | IWmsLayerGroupConfig => {
        let layerConfig: IVectorLayerGroupConfig | IWmsLayerGroupConfig = null;

        switch (recordType) {
            case KentRecordType.Monument:
                layerConfig = this.Monument(opts);
                break;         

            case KentRecordType.AreaBoundary:
                layerConfig = this.AreaBoundary();
                break;

            case KentRecordType.HistoricLandscapeCharacterisation:
                layerConfig = this.HLC(opts);
                break;
        }

        if (layerConfig === null && recordSubType !== null) {
            switch (recordSubType) {

                case KentRecordSubType.ListedBuilding:
                    layerConfig = this.ListedBuilding(opts);
                    break;  

                case KentRecordSubType.ScheduledMonument:
                    layerConfig = this.ScheduledMonument(opts);
                    break;    
                                
                case KentRecordSubType.RegisteredParksAndGardens:
                    layerConfig = this.RegisteredParksAndGardens(opts);
                    break; 

                case KentRecordSubType.HistoricParksAndGardens:
                    layerConfig = this.HistoricParksAndGardens(opts);
                    break; 

                case KentRecordSubType.ConservationArea:
                    layerConfig = this.ConservationArea(opts);
                    break;    

                //DoverUAD
                case KentRecordSubType.AngloSaxon:
                    layerConfig = this.AngloSaxon(opts);
                    break;

                case KentRecordSubType.EarlyRoman:
                    layerConfig = this.EarlyRoman(opts);
                    break;

                case KentRecordSubType.LateRoman:
                    layerConfig = this.LateRoman(opts);
                    break;

                case KentRecordSubType.Harbour1500:
                    layerConfig = this.Harbour1500(opts);
                    break;

                case KentRecordSubType.Medieval:
                    layerConfig = this.Medieval(opts);
                    break;

                case KentRecordSubType.PostMedieval1638to1641:
                    layerConfig = this.PostMedieval1638to1641(opts);
                    break;

                case KentRecordSubType.PostMedieval1865:
                    layerConfig = this.PostMedieval1865(opts);
                    break;

                case KentRecordSubType.PostMedieval1908:
                    layerConfig = this.PostMedieval1908(opts);
                    break;

            }
        }

        return layerConfig;
    }

    private AreaBoundary = (): IWmsLayerGroupConfig => {
        return {
            displayName: "Kent Boundary",
            ref: "AreaBoundary",
            type: KentRecordType.AreaBoundary,
            url: this.mapFileUrl,
            displayInLayerSwitcher: false,
            displayInLegend: false,
            visibleOnLoad: true,
            queryable: false,
            layers: [
                {
                    layerName: "AreaBoundary",
                    minZoom: 1,
                    maxZoom: 19
                }
            ]
        }
    }

    private Monument = (
        opts: IVectorLayerGroupConfig
    ): IVectorLayerGroupConfig => {
        return {
            ...{
                displayName: "Monuments",
                ref: "Monuments",
                type: KentRecordType.Monument,
                strategy: VectorLayerStrategy.Bbox,
                url: this.mapFileUrl,
                wfsParams: this.paramSource(),
                maxFeatureCount: 2000,
                legend: { line: false, polygon: false },
                visibleOnFirstLoad: false,
                alwaysLoadOnZoom: true,
                layers: [
                    // Kent requested to display points only
                    /*{
                        layerName: "MonumentPolygonsWFS",
                        minZoom: 1,
                        maxZoom: 19
                    },
                    {
                        layerName: "MonumentLinesWFS",
                        minZoom: 1,
                        maxZoom: 19
                    },*/
                    {
                        layerName: "MonumentPointsWFS",
                        minZoom: 1,
                        maxZoom: 19
                    }                    
                ]
            }, ...opts
        };
    }

    private ListedBuilding = (
        opts: IVectorLayerGroupConfig
    ): IVectorLayerGroupConfig => {
        return {
            ...{
                displayName: "Listed Buildings",
                ref: "ListedBuildings",
                type: KentRecordType.Designation,
                subType: KentRecordSubType.ListedBuilding,
                strategy: VectorLayerStrategy.Bbox,
                url: this.mapFileUrl,
                urlParams: { recsubtypecode: KentRecordSubType.ListedBuilding },
                wfsParams: this.paramSource(),
                maxFeatureCount: 10000,
                visibleOnFirstLoad: false,
                alwaysLoadOnZoom: true,
                layers: [
                    {
                        layerName: "DesignationPolygonsWFS",
                        minZoom: 1,
                        maxZoom: 19
                    },
                    {
                        layerName: "DesignationLinesWFS",
                        minZoom: 1,
                        maxZoom: 19
                    },
                    {
                        layerName: "DesignationPointsWFS",
                        minZoom: 1,
                        maxZoom: 19
                    }
                ]
            }, ...opts
        };
    }

    private ScheduledMonument = (
        opts: IVectorLayerGroupConfig
    ): IVectorLayerGroupConfig => {
        return {
            ...{
                displayName: "Scheduled Monuments",
                ref: "ScheduledMonuments",
                type: KentRecordType.Designation,
                subType: KentRecordSubType.ScheduledMonument,
                strategy: VectorLayerStrategy.Bbox,
                url: this.mapFileUrl,
                urlParams: { recsubtypecode: KentRecordSubType.ScheduledMonument },
                wfsParams: this.paramSource(),
                maxFeatureCount: 10000,
                legend: { line: false, point: false },
                visibleOnFirstLoad: false,
                alwaysLoadOnZoom: true,
                layers: [
                    {
                        layerName: "DesignationPolygonsWFS",
                        minZoom: 1,
                        maxZoom: 19
                    }
                ]
            }, ...opts
        };
    }
 
    private RegisteredParksAndGardens = (
        opts: IVectorLayerGroupConfig
    ): IVectorLayerGroupConfig => {
        return {
            ...{
                displayName: "Registered Parks and Gardens",
                ref: "RegisteredParksAndGardens",
                type: KentRecordType.Designation,
                subType: KentRecordSubType.RegisteredParksAndGardens,
                strategy: VectorLayerStrategy.Bbox,
                url: this.mapFileUrl,
                urlParams: { recsubtypecode: KentRecordSubType.RegisteredParksAndGardens },
                wfsParams: this.paramSource(),
                maxFeatureCount: 10000,
                legend: { line: false, point: false },
                visibleOnFirstLoad: false,
                alwaysLoadOnZoom: true,
                layers: [
                    {
                        layerName: "DesignationPolygonsWFS",
                        minZoom: 1,
                        maxZoom: 19
                    }
                ]
            }, ...opts
        };
    }

    private HistoricParksAndGardens = (
        opts: IVectorLayerGroupConfig
    ): IVectorLayerGroupConfig => {
        return {
            ...{
                displayName: "Historic Parks and Gardens",
                ref: "HistoricParksAndGardens",
                type: KentRecordType.Designation,
                subType: KentRecordSubType.HistoricParksAndGardens,
                strategy: VectorLayerStrategy.Bbox,
                url: this.mapFileUrl,
                urlParams: { recsubtypecode: KentRecordSubType.HistoricParksAndGardens },
                wfsParams: this.paramSource(),
                maxFeatureCount: 10000,
                legend: { line: false, point: false },
                visibleOnFirstLoad: false,
                alwaysLoadOnZoom: true,
                layers: [
                    {
                        layerName: "DesignationPolygonsWFS",
                        minZoom: 1,
                        maxZoom: 19
                    }
                ]
            }, ...opts
        };
    }

    
    private ConservationArea = (
        opts: IVectorLayerGroupConfig
    ): IVectorLayerGroupConfig => {
        return {
            ...{
                displayName: "Conservation Areas",
                ref: "ConservationArea",
                type: KentRecordType.Designation,
                subType: KentRecordSubType.ConservationArea,
                strategy: VectorLayerStrategy.Bbox,
                url: this.mapFileUrl,
                wfsParams: this.paramSource(),
                maxFeatureCount: 10000,
                legend: { line: false, point: false },
                visibleOnFirstLoad: false,
                alwaysLoadOnZoom: true,
                layers: [
                    {
                        layerName: "ConservationAreasWFS",
                        minZoom: 1,
                        maxZoom: 19
                    }
                ]
            }, ...opts
        };
    }

    private HLC = (
        opts: IVectorLayerGroupConfig
    ): IVectorLayerGroupConfig => {
        return {
            ...{
                displayName: "Historic Landscape Characterisation",
                ref: "HLC",
                type: KentRecordType.HistoricLandscapeCharacterisation,
                subType: null,
                strategy: VectorLayerStrategy.Bbox,
                url: this.mapFileUrl,
                wfsParams: this.paramSource(),
                maxFeatureCount: 10000,
                legend: { line: false, point: false },
                visibleOnFirstLoad: false,
                alwaysLoadOnZoom: true,
                overrideFeatureAdd: new FeatureAdd(),
                layers: [
                    {
                        layerName: "HLCPolygonsWFS",
                        minZoom: 1,
                        maxZoom: 19
                    }
                ]
            }, ...opts
        };
    }

    //Dover UAD
    private AngloSaxon = (
        opts: IVectorLayerGroupConfig
    ): IVectorLayerGroupConfig => {
        return {
            ...{
                displayName: "Dover UAD - Anglo Saxon",
                ref: "DoverUAD_AngloSaxon",
                type: KentRecordType.DoverCharacterisation,
                subType: KentRecordSubType.AngloSaxon,
                strategy: VectorLayerStrategy.Bbox,
                url: this.mapFileUrl,
                wfsParams: this.paramSource(),
                maxFeatureCount: 10000,
                legend: { line: false, point: false },
                visibleOnFirstLoad: false,
                alwaysLoadOnZoom: true,
                overrideFeatureAdd: new FeatureAdd(),
                layers: [
                    {
                        layerName: "DoverUADAngloSaxonPolygonsWFS",
                        minZoom: 1,
                        maxZoom: 19
                    }
                ]
            }, ...opts
        };
    }

    private EarlyRoman = (
        opts: IVectorLayerGroupConfig
    ): IVectorLayerGroupConfig => {
        return {
            ...{
                displayName: "Dover UAD - Early Roman",
                ref: "DoverUAD_EarlyRoman",
                type: KentRecordType.DoverCharacterisation,
                subType: KentRecordSubType.EarlyRoman,
                strategy: VectorLayerStrategy.Bbox,
                url: this.mapFileUrl,
                wfsParams: this.paramSource(),
                maxFeatureCount: 10000,
                legend: { point: false },
                visibleOnFirstLoad: false,
                alwaysLoadOnZoom: true,
                overrideFeatureAdd: new FeatureAdd(),
                layers: [
                    {
                        layerName: "DoverUADEarlyRomanPolygonsWFS",
                        minZoom: 1,
                        maxZoom: 19
                    },
                    {
                        layerName: "DoverUADEarlyRomanLinesWFS",
                        minZoom: 1,
                        maxZoom: 19
                    }
                ]
            }, ...opts
        };
    }

    private LateRoman = (
        opts: IVectorLayerGroupConfig
    ): IVectorLayerGroupConfig => {
        return {
            ...{
                displayName: "Dover UAD - Late Roman",
                ref: "DoverUAD_LateRoman",
                type: KentRecordType.DoverCharacterisation,
                subType: KentRecordSubType.LateRoman,
                strategy: VectorLayerStrategy.Bbox,
                url: this.mapFileUrl,
                wfsParams: this.paramSource(),
                maxFeatureCount: 10000,
                legend: { point: false },
                visibleOnFirstLoad: false,
                alwaysLoadOnZoom: true,
                overrideFeatureAdd: new FeatureAdd(),
                layers: [
                    {
                        layerName: "DoverUADLateRomanPolygonsWFS",
                        minZoom: 1,
                        maxZoom: 19
                    },
                    {
                        layerName: "DoverUADLateRomanLinesWFS",
                        minZoom: 1,
                        maxZoom: 19
                    }
                ]
            }, ...opts
        };
    }

    private Harbour1500 = (
        opts: IVectorLayerGroupConfig
    ): IVectorLayerGroupConfig => {
        return {
            ...{
                displayName: "Dover UAD - Harbour to 1500",
                ref: "DoverUAD_Harbour1500",
                type: KentRecordType.DoverCharacterisation,
                subType: KentRecordSubType.Harbour1500,
                strategy: VectorLayerStrategy.Bbox,
                url: this.mapFileUrl,
                wfsParams: this.paramSource(),
                maxFeatureCount: 10000,
                legend: { line: false, point: false },
                visibleOnFirstLoad: false,
                alwaysLoadOnZoom: true,
                overrideFeatureAdd: new FeatureAdd(),
                layers: [
                    {
                        layerName: "DoverUADHarbour1500PolygonsWFS",
                        minZoom: 1,
                        maxZoom: 19
                    }
                ]
            }, ...opts
        };
    }

    private Medieval = (
        opts: IVectorLayerGroupConfig
    ): IVectorLayerGroupConfig => {
        return {
            ...{
                displayName: "Dover UAD - Medieval",
                ref: "DoverUAD_Medieval",
                type: KentRecordType.DoverCharacterisation,
                subType: KentRecordSubType.Medieval,
                strategy: VectorLayerStrategy.Bbox,
                url: this.mapFileUrl,
                wfsParams: this.paramSource(),
                maxFeatureCount: 10000,
                legend: { line: false, point: false },
                visibleOnFirstLoad: false,
                alwaysLoadOnZoom: true,
                overrideFeatureAdd: new FeatureAdd(),
                layers: [
                    {
                        layerName: "DoverUADMedievalPolygonsWFS",
                        minZoom: 1,
                        maxZoom: 19
                    },
                    {
                        layerName: "DoverUADMedievalLinesWFS",
                        minZoom: 1,
                        maxZoom: 19
                    }
                ]
            }, ...opts
        };
    }

    private PostMedieval1638to1641 = (
        opts: IVectorLayerGroupConfig
    ): IVectorLayerGroupConfig => {
        return {
            ...{
                displayName: "Dover UAD - Post-Medieval 1638-1641",
                ref: "DoverUAD_PostMedieval1641",
                type: KentRecordType.DoverCharacterisation,
                subType: KentRecordSubType.PostMedieval1638to1641,
                strategy: VectorLayerStrategy.Bbox,
                url: this.mapFileUrl,
                wfsParams: this.paramSource(),
                maxFeatureCount: 10000,
                legend: { line: false, point: false },
                visibleOnFirstLoad: false,
                alwaysLoadOnZoom: true,
                overrideFeatureAdd: new FeatureAdd(),
                layers: [
                    {
                        layerName: "DoverUADPostMedieval1638to1641PolygonsWFS",
                        minZoom: 1,
                        maxZoom: 19
                    }
                ]
            }, ...opts
        };
    }

    private PostMedieval1865 = (
        opts: IVectorLayerGroupConfig
    ): IVectorLayerGroupConfig => {
        return {
            ...{
                displayName: "Dover UAD - Post-Medieval 1865",
                ref: "DoverUAD_PostMedieval1865",
                type: KentRecordType.DoverCharacterisation,
                subType: KentRecordSubType.PostMedieval1865,
                strategy: VectorLayerStrategy.Bbox,
                url: this.mapFileUrl,
                wfsParams: this.paramSource(),
                maxFeatureCount: 10000,
                legend: { line: false, point: false },
                visibleOnFirstLoad: false,
                alwaysLoadOnZoom: true,
                overrideFeatureAdd: new FeatureAdd(),
                layers: [
                    {
                        layerName: "DoverUADPostMedieval1865PolygonsWFS",
                        minZoom: 1,
                        maxZoom: 19
                    }
                ]
            }, ...opts
        };
    }

    private PostMedieval1908 = (
        opts: IVectorLayerGroupConfig
    ): IVectorLayerGroupConfig => {
        return {
            ...{
                displayName: "Dover UAD - Post-Medieval 1908",
                ref: "DoverUAD_PostMedieval1908",
                type: KentRecordType.DoverCharacterisation,
                subType: KentRecordSubType.PostMedieval1908,
                strategy: VectorLayerStrategy.Bbox,
                url: this.mapFileUrl,
                wfsParams: this.paramSource(),
                maxFeatureCount: 10000,
                legend: { line: false, point: false },
                visibleOnFirstLoad: false,
                alwaysLoadOnZoom: true,
                overrideFeatureAdd: new FeatureAdd(),
                layers: [
                    {
                        layerName: "DoverUADPostMedieval1908PolygonsWFS",
                        minZoom: 1,
                        maxZoom: 19
                    }
                ]
            }, ...opts
        };
    }
}

